/**
 * PDF1 Slide 5 - Resumo de Visitas - Dados Relevantes
 * Endpoint: /api/v1/visitas/dashboards/dados-relevantes/
 * 
 * Filtros: ANO + MÊS em ambos os gráficos
 */
import { useState, useEffect } from "react";
import { Card, Row, Col, Spinner, Alert, Badge } from "react-bootstrap";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import api from "../../../services/api";
import { MESES_COMPLETOS } from "./utils/mesesUtils";

const Slide5DadosRelevantes = ({ programaId, filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const ano = filters?.year || new Date().getFullYear();
  const mes = filters?.month;

  useEffect(() => {
    let url = `/visitas/dashboards/dados-relevantes/?programa_id=${programaId}&ano=${ano}`;
    if (mes !== null && mes !== undefined) {
      url += `&mes=${mes}`;
    }
    
    api.get(url)
      .then(res => {
        if (res.data) {
          setData(res.data);
        } else {
          setData({ kpi_total: 0, kpi_com_dados: 0, dados_pie: [], dados_relevantes: [] });
        }
      })
      .catch((err) => {
        console.error('[Slide5] API error:', err.response?.data || err.message);
        setError(err.response?.data?.error || err.message);
      })
      .finally(() => setLoading(false));
  }, [programaId, ano, mes]);

  const labelAnoMes = mes ? `${MESES_COMPLETOS[mes]} ${ano}` : `Ano: ${ano}`;

  if (loading) return <Spinner animation="border" className="d-block mx-auto my-4" />;

  if (error) {
    return (
      <Alert variant="warning" className="m-3">
        <strong>Seção 4 - Dados Relevantes:</strong> {error}
      </Alert>
    );
  }

  const dadosPieArr = Array.isArray(data?.dados_pie) ? data.dados_pie : [];
  const dadosRelevantesArr = Array.isArray(data?.dados_relevantes) ? data.dados_relevantes : [];
  if (data && (data.dados_pie !== undefined && !Array.isArray(data.dados_pie))) {
    console.warn('[Slide5] dados_pie não é array:', data.dados_pie);
  }
  if (data && (data.dados_relevantes !== undefined && !Array.isArray(data.dados_relevantes))) {
    console.warn('[Slide5] dados_relevantes não é array:', data.dados_relevantes);
  }
  const hasPie = dadosPieArr.length > 0 || (data?.kpi_total || 0) > 0;
  const hasDados = dadosRelevantesArr.length > 0;

  const pieData = hasPie ? [
    {
      id: "Com Dados",
      label: "Com Dados",
      value: dadosPieArr.find(d => d?.id === "Com Dados")?.value || data?.kpi_com_dados || 0,
      color: "#ed7d31"
    },
    {
      id: "Sem Dados",
      label: "Sem Dados",
      value: dadosPieArr.find(d => d?.id === "Sem Dados")?.value || ((data?.kpi_total || 0) - (data?.kpi_com_dados || 0)) || 0,
      color: "#5b9bd5"
    }
  ] : [];

  const CenteredMetric = ({ centerX, centerY }) => (
    <>
      <text
        x={centerX}
        y={centerY - 10}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: "24px", fontWeight: "bold", fill: "#333" }}
      >
        {data?.kpi_total || 0}
      </text>
      <text
        x={centerX}
        y={centerY + 12}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: "11px", fill: "#666" }}
      >
        Total
      </text>
    </>
  );

  return (
    <Row>
      {/* Gráfico Pizza - Filtro: ANO + MÊS */}
      <Col md={4} className="mb-4">
        <Card className="h-100 border-0 shadow-sm">
          <Card.Header className="bg-white fw-bold border-0 d-flex justify-content-between align-items-center">
            <span>Visitas e Dados Relevantes</span>
            <Badge bg="primary" className="fw-normal">{labelAnoMes}</Badge>
          </Card.Header>
          <Card.Body style={{ height: "420px" }}>
            {hasPie ? (
              <ResponsivePie
                data={pieData}
                margin={{ top: 30, right: 20, bottom: 80, left: 20 }}
                innerRadius={0.55}
                padAngle={0.7}
                cornerRadius={3}
                colors={{ datum: "data.color" }}
                enableArcLinkLabels={false}
                arcLabelsTextColor="#fff"
                layers={['arcs', 'arcLabels', 'legends', CenteredMetric]}
                legends={[
                  {
                    anchor: "bottom",
                    direction: "row",
                    translateY: 50,
                    itemWidth: 100,
                    itemHeight: 20,
                    symbolSize: 14,
                    symbolShape: "circle"
                  }
                ]}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100">
                <span className="text-muted">Sem registros disponíveis</span>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      {/* Análise de Dados Relevantes - Filtro: ANO + MÊS */}
      <Col md={8} className="mb-4">
        <Card className="h-100 border-0 shadow-sm">
          <Card.Header className="bg-white fw-bold border-0 d-flex justify-content-between align-items-center">
            <span>Análise de Dados Relevantes</span>
            <Badge bg="primary" className="fw-normal">{labelAnoMes}</Badge>
          </Card.Header>
          <Card.Body style={{ height: "420px" }}>
            {hasDados ? (
              <ResponsiveBar
                data={dadosRelevantesArr}
                keys={["valor"]}
                indexBy="categoria"
                layout="horizontal"
                margin={{ top: 10, right: 40, bottom: 30, left: 180 }}
                padding={0.3}
                colors={["#5b9bd5"]}
                labelTextColor="#fff"
                axisLeft={{
                  tickSize: 0,
                  tickPadding: 10
                }}
                axisBottom={{
                  tickSize: 0,
                  tickPadding: 5
                }}
                enableGridX={true}
                enableGridY={false}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100">
                <span className="text-muted">Sem registros disponíveis</span>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default Slide5DadosRelevantes;
